import { useEffect, useState, useContext } from 'react';
import { useSelector } from 'react-redux';

import { HiProPaymentService } from 'app/services/hiProPayment.services';
import { LabelEnum, MessageTypeEnum, InstallmentsEnum } from 'app/shared/enums';
import { useNotification } from 'app/shared/hooks/useNotification';
import { selectSelectedOfferId } from 'app/store/data/selectedOffer';
import { getOffer } from 'app/store/data/quotationResult';
import { MubiInstallmentsContext } from 'app/components/MubiInstallmentsRadioOption/useMubiInstallments.context';

import {
  ErrorDecision,
  SmsErrors,
  getMubiInstalmentError,
} from 'app/shared/helpers/getMubiInstalmentError';
import { useMubiInstallments } from 'app/components/MubiInstallmentsRadioOption/useMubiInstallments';

export default function useApplicationSmsForm(isConfirmApplication: boolean) {
  const notify = useNotification();
  const {
    hiProToken,
    closeModal,
    setErrorOnModal,
    mubiInstallmentsDataLayerEvent,
    setActiveStep,
  } = useContext(MubiInstallmentsContext);
  const offerId = useSelector(selectSelectedOfferId);

  const offer = useSelector(getOffer(offerId));
  const { clearTokenState } = useMubiInstallments(offer);

  const [value, setValue] = useState('');
  const [canSendAgain, setCanSendAgain] = useState(false);
  const [isSmsNotValid, setIsSmsNotValid] = useState(false);
  const [alertError, setAlertError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const sendCodeAgain = async () => {
    if (!hiProToken) {
      return;
    }

    setCanSendAgain(false);

    if (isConfirmApplication) {
      mubiInstallmentsDataLayerEvent(
        InstallmentsEnum.EventNameFirstSmsResend,
        InstallmentsEnum.EventStepNameFirstConsentSms
      );
    } else {
      mubiInstallmentsDataLayerEvent(
        InstallmentsEnum.EventNameSecondSmsResend,
        InstallmentsEnum.EventStepNameSecondConsentSms
      );
    }

    clearAllErrors();
    await HiProPaymentService.retryGetSmsCode({
      token: hiProToken,
    })
      .catch(({ response }) => {
        if (response?.data) {
          handleErrorState(response.data.errorKey || response.data.message);
        }
      })
      .finally(() => {
        setTimeout(() => setCanSendAgain(true), 30000);
      });
  };

  const clearAllErrors = () => {
    setIsSmsNotValid(false);
    setAlertError(false);
  };

  const handleErrorState = (errorKey: string) => {
    const hiProError = getMubiInstalmentError(errorKey);

    if (!hiProError) {
      notify(MessageTypeEnum.Danger, LabelEnum.ServerError);
    } else if (hiProError === SmsErrors.InvalidPin) {
      setIsSmsNotValid(true);
    } else if (hiProError === SmsErrors.InvalidPinRequestNew) {
      setAlertError(true);
    } else if (hiProError === ErrorDecision.NegativeDecision) {
      setActiveStep(7);
    } else {
      closeModal();
      setErrorOnModal(hiProError);
      clearTokenState();
    }
  };

  const onSubmitForm = async () => {
    if (!hiProToken) {
      return;
    }

    if (!value) {
      setIsSmsNotValid(true);
      return;
    }

    clearAllErrors();

    setIsLoading(true);
    if (isConfirmApplication) {
      mubiInstallmentsDataLayerEvent(
        InstallmentsEnum.EventNameFirstSmsConsentNextStep,
        InstallmentsEnum.EventStepNameFirstConsentSms
      );

      await HiProPaymentService.verificationHiProApplicationSms({
        token: hiProToken,
        smsValue: value,
      })
        .then(() => {
          setActiveStep(3);
        })
        .catch(({ response }) => {
          if (response?.data) {
            handleErrorState(response.data.errorKey);
          }

          setIsLoading(false);
        });
    } else {
      mubiInstallmentsDataLayerEvent(
        InstallmentsEnum.EventNameContractSign,
        InstallmentsEnum.EventStepNameSecondConsentSms
      );
      await HiProPaymentService.confirmHiProContractSms({
        token: hiProToken,
        smsValue: value,
      })
        .then(() => {
          setActiveStep(6);
        })
        .catch(({ response }) => {
          if (response?.data) {
            handleErrorState(response.data.errorKey);
          }

          setIsLoading(false);
        });
    }
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let { value } = event.target;
    const valueContainsOnlyNumbers = /^[0-9\b]+$/.test(value);

    setIsSmsNotValid(!valueContainsOnlyNumbers);

    setValue(value);
  };

  useEffect(() => {
    setTimeout(() => setCanSendAgain(true), 30000);
    if (isConfirmApplication) {
      mubiInstallmentsDataLayerEvent(
        InstallmentsEnum.EventModalStep,
        InstallmentsEnum.EventStepNameFirstConsentSms
      );
    } else {
      mubiInstallmentsDataLayerEvent(
        InstallmentsEnum.EventModalStep,
        InstallmentsEnum.EventStepNameSecondConsentSms
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    sendCodeAgain,
    onSubmitForm,
    value,
    handleOnChange,
    canSendAgain,
    isSmsNotValid,
    alertError,
    isLoading,
  };
}
